import { useEffect, useState } from 'react';
import { Outlet, useLocation } from 'react-router-dom';
import { Container, useMediaQuery } from '@mui/material';
import { useTheme } from '@mui/material/styles';

import { colors } from '@src/styles';
import {
  SidebarContainer,
  SIDEBAR_COLLAPSED_WIDTH,
  SIDEBAR_EXPANDED_WIDTH,
  MOBILE_TOPBAR_HEIGHT,
  readSidebarExpanded,
  saveSidebarExpanded,
} from '@src/components/Sidebar';
import { BrainApi } from '@src/services/brain/brain-api';
import { GetMeResponseDTO } from '@src/services/brain/modules/v1/auth/dto/brain-auth.getMe.response.dto';
import { LutienApi } from '@src/services/lutien/lutien-api';

export type TCurrentPages = 'home' | 'automations' | 'tasks' | 'canonicals' | 'org-config' | 'scripts' | 'runs';

// Mapeamento de rotas para páginas
const ROUTE_TO_PAGE_MAP: Record<string, TCurrentPages> = {
  '/automations': 'automations',
  '/tasks': 'tasks',
  '/canonicals': 'canonicals',
  '/org-config': 'org-config',
  '/scripts': 'scripts',
  '/runs': 'runs',
};

export interface UserData extends GetMeResponseDTO {
  name: string;
  email: string;
  organizations: { id: string; fantasyName: string }[];
}

interface LayoutProps {
  showNavbar?: boolean;
  noMargin?: boolean;
  calculateHeight?: boolean;
}

export const Layout = ({ showNavbar = true, noMargin = false, calculateHeight = true }: LayoutProps) => {
  const location = useLocation();
  const [userData, setUserData] = useState<UserData | null>(null);
  const [isLoadingUser, setIsLoadingUser] = useState(true);
  const [isSidebarExpanded, setIsSidebarExpanded] = useState<boolean>(readSidebarExpanded);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  useEffect(() => {
    setIsMobileMenuOpen(false);
  }, [location.pathname]);

  const handleToggleSidebar = () => {
    setIsSidebarExpanded((previous) => {
      saveSidebarExpanded(!previous);
      return !previous;
    });
  };
  const gutter = noMargin ? 0 : isMobile ? 12 : 16;
  const sidebarWidth = isSidebarExpanded ? SIDEBAR_EXPANDED_WIDTH : SIDEBAR_COLLAPSED_WIDTH;
  const sidebarOffset = showNavbar && !isMobile ? sidebarWidth : 0;
  const topOffset = showNavbar && isMobile ? MOBILE_TOPBAR_HEIGHT : 0;
  const heightVariant = calculateHeight
    ? { minHeight: `calc(100vh - ${topOffset + gutter * 2}px)` }
    : { minHeight: '100vh' };

  useEffect(() => {
    const fetchUserData = async () => {
      try {
        const brainApi = new BrainApi();
        const me = await brainApi.v1.auth.getMe();
        const lutienApi = new LutienApi();
        const user = await lutienApi.v1.users.getById({ id: me.id });

        const organizations = user.memberships.map((membership) => ({
          id: membership.organization.id,
          fantasyName: membership.organization.fantasyName,
        }));
        const name = `${user.name} ${user.lastName ?? ''}`.trim();

        setUserData({ ...me, name, email: user.email, organizations });
      } catch (error) {
        console.error('Failed to fetch user data:', error);
      } finally {
        setIsLoadingUser(false);
      }
    };

    fetchUserData();
  }, []);

  const getCurrentPage = (): TCurrentPages => {
    const matchedPage = Object.entries(ROUTE_TO_PAGE_MAP).find(([route]) => location.pathname.startsWith(route));
    return matchedPage ? matchedPage[1] : 'home';
  };

  return (
    <Container
      disableGutters
      maxWidth={false}
      sx={{
        backgroundColor: colors.background.primary,
        padding: '0px',
      }}
    >
      {showNavbar && (
        <SidebarContainer
          currentPage={getCurrentPage()}
          userData={userData}
          isLoadingUser={isLoadingUser}
          isExpanded={isSidebarExpanded}
          onToggleExpanded={handleToggleSidebar}
          isMobile={isMobile}
          isMobileOpen={isMobileMenuOpen}
          onOpenMobile={() => setIsMobileMenuOpen(true)}
          onCloseMobile={() => setIsMobileMenuOpen(false)}
        />
      )}
      <Container
        disableGutters
        maxWidth={false}
        sx={{
          width: `calc(100vw - ${sidebarOffset + gutter * 2}px)`,
          marginTop: `${topOffset + gutter}px`,
          marginRight: `${gutter}px`,
          marginBottom: `${gutter}px`,
          marginLeft: `${sidebarOffset + gutter}px`,
          transition: 'margin-left 0.18s ease, width 0.18s ease',
          ...heightVariant,
        }}
      >
        <Outlet />
      </Container>
    </Container>
  );
};
